/**
 * Road Utilities
 * Centralized utilities for road classification, formatting, and validation
 */

import {
  ROAD_PATTERNS,
  ROAD_PREFIXES,
  RoadType,
  TRANSLATION_PATTERNS,
  isNationalRoadNumber,
  isVoivodeshipRoadNumber,
  ROAD_TRANSLATION_KEYS,
} from '../constants/roadConstants';

// Re-export RoadType for external use
export { RoadType } from '../constants/roadConstants';

export interface RoadReference {
  shortName: string;
  type: RoadType;
  originalInput: string;
}

// Re-export getRoadType and RoadTypeInfo from canonical source
export { getRoadType, type RoadTypeInfo } from './roadTypeHelpers';

/**
 * Extract road reference from various input formats
 * Handles prefixed roads (A1, S1, DK1, DW305) and plain numbers (1, 305)
 */
export function extractRoadReference(input: string): string | null {
  if (!input?.trim()) return null;

  const trimmedInput = input.trim();

  // Check for complete road references first
  for (const [, pattern] of Object.entries(ROAD_PATTERNS)) {
    if (
      pattern === ROAD_PATTERNS.THREE_DIGIT_NUMBER ||
      pattern === ROAD_PATTERNS.ONE_TWO_DIGIT_NUMBER
    ) {
      continue; // Skip plain number patterns for now
    }

    const match = trimmedInput.match(pattern);
    if (match?.[1]) {
      return match[1].toUpperCase();
    }
  }

  // Handle plain 3-digit numbers (voivodeship roads)
  const threeDigitMatch = trimmedInput.match(ROAD_PATTERNS.THREE_DIGIT_NUMBER);
  if (threeDigitMatch?.[1]) {
    const number = parseInt(threeDigitMatch[1], 10);
    if (isVoivodeshipRoadNumber(number)) {
      return `${ROAD_PREFIXES.VOIVODESHIP}${number}`;
    }
  }

  // Handle plain 1-2 digit numbers (national roads)
  const shortNumberMatch = trimmedInput.match(
    ROAD_PATTERNS.ONE_TWO_DIGIT_NUMBER
  );
  if (shortNumberMatch?.[1]) {
    const number = parseInt(shortNumberMatch[1], 10);
    if (isNationalRoadNumber(number)) {
      return `${ROAD_PREFIXES.NATIONAL}${number}`;
    }
  }

  return null;
}

/**
 * Extract shortName from translated format or various input formats
 * Handles both new shortName format and old translated formats
 */
export function extractShortNameFromInput(value: string): string {
  if (!value?.trim()) return '';

  const trimmedValue = value.trim();

  // If it's already a shortName, return as-is
  if (ROAD_PATTERNS.COMPLETE_ROAD_REFERENCE.test(trimmedValue)) {
    return trimmedValue.toUpperCase();
  }

  // Handle plain numbers
  const plainNumberMatch = trimmedValue.match(/^(\d+)$/);
  if (plainNumberMatch?.[1]) {
    const number = parseInt(plainNumberMatch[1], 10);
    if (isVoivodeshipRoadNumber(number)) {
      return `${ROAD_PREFIXES.VOIVODESHIP}${number}`;
    }
    if (isNationalRoadNumber(number)) {
      return `${ROAD_PREFIXES.NATIONAL}${number}`;
    }
  }

  // Try to extract from translated format
  for (const pattern of TRANSLATION_PATTERNS) {
    const match = trimmedValue.match(pattern);
    if (match?.[1]) {
      const roadNumber = match[1];
      if (pattern.source.includes('krajowa') && /^\d+$/.test(roadNumber)) {
        return `${ROAD_PREFIXES.NATIONAL}${roadNumber}`;
      }
      if (pattern.source.includes('wojewódzka') && /^\d+$/.test(roadNumber)) {
        return `${ROAD_PREFIXES.VOIVODESHIP}${roadNumber}`;
      }
      return roadNumber.toUpperCase();
    }
  }

  return value; // Return original if no pattern matches
}

/**
 * Determine road type from shortName
 */
export function getRoadTypeFromShortName(shortName: string): RoadType | null {
  if (!shortName) return null;

  const upperShortName = shortName.toUpperCase();

  if (upperShortName.startsWith(ROAD_PREFIXES.MOTORWAY)) {
    return RoadType.MOTORWAY;
  }
  if (upperShortName.startsWith(ROAD_PREFIXES.EXPRESSWAY)) {
    return RoadType.EXPRESSWAY;
  }
  if (upperShortName.startsWith(ROAD_PREFIXES.NATIONAL)) {
    return RoadType.NATIONAL_ROAD;
  }
  if (upperShortName.startsWith(ROAD_PREFIXES.VOIVODESHIP)) {
    return RoadType.VOIVODESHIP_ROAD;
  }

  return null;
}

/**
 * Get translated road name for display
 */
export function getTranslatedRoadName(
  shortName: string,
  roadType: RoadType,
  t: (key: string) => string
): string {
  const translationKey = ROAD_TRANSLATION_KEYS[roadType];

  if (!translationKey) {
    return shortName;
  }

  switch (roadType) {
    case RoadType.MOTORWAY:
    case RoadType.EXPRESSWAY:
      return `${t(translationKey)} ${shortName}`;
    case RoadType.NATIONAL_ROAD:
      return `${t(translationKey)} ${shortName}`;
    case RoadType.VOIVODESHIP_ROAD:
      return `${t(translationKey)} ${shortName}`;
    default:
      return shortName;
  }
}

/**
 * Format road name for display with proper translation
 * Handles roads from any source (suggestions, map, GPS, manual)
 */
export function formatRoadDisplayName(
  input: string,
  roadTypeMap: Map<string, string>,
  t: (key: string) => string
): string {
  if (!input?.trim()) return '';

  // Check if already translated to prevent duplication (GPS suggestions)
  if (isAlreadyTranslatedRoadName(input)) {
    return input; // Return as-is if already contains Polish road type prefix
  }

  const shortName = extractShortNameFromInput(input);

  // Check if it's a known road from suggestions
  const knownRoadType = roadTypeMap.get(shortName);
  if (knownRoadType) {
    return getTranslatedRoadName(shortName, knownRoadType as RoadType, t);
  }

  // Try to detect type from pattern for roads not in suggestions
  const detectedType = getRoadTypeFromShortName(shortName);
  if (detectedType) {
    return getTranslatedRoadName(shortName, detectedType, t);
  }

  // Return original if no pattern matches
  return input;
}

/**
 * Get preferred road name for display (used in GPS suggestions)
 * Ensures proper formatting for different road types
 */
export function getPreferredRoadName(road: {
  shortName?: string;
  ref?: string;
  name?: string;
  type?: string;
}): string {
  // Prefer shortName as it's already properly formatted
  if (road.shortName) {
    return road.shortName;
  }

  // If we only have a ref, try to format it properly
  if (road.ref) {
    // If ref is just a number and it's a voivodeship road, add DW prefix
    if (road.type === 'voivodeship' && /^\d{3}$/.test(road.ref)) {
      return `${ROAD_PREFIXES.VOIVODESHIP}${road.ref}`;
    }
    // If ref is just a number and it's a national road, add DK prefix
    if (road.type === 'national' && /^\d{1,2}$/.test(road.ref)) {
      return `${ROAD_PREFIXES.NATIONAL}${road.ref}`;
    }
    return road.ref;
  }

  return road.name ?? 'Unknown Road';
}

/**
 * Check if a road name already contains a translated road type prefix
 * Used to prevent double translation of GPS suggestions
 */
export function isAlreadyTranslatedRoadName(input: string): boolean {
  if (!input?.trim()) return false;

  const trimmedInput = input.trim();

  // Check for Polish road type prefixes
  const translatedPrefixes = [
    'Autostrada', // Motorway
    'Droga ekspresowa', // Expressway
    'Droga krajowa', // National road
    'Droga wojewódzka', // Voivodeship road
  ];

  return translatedPrefixes.some(
    (prefix) => trimmedInput.startsWith(prefix + ' ') || trimmedInput === prefix
  );
}

/**
 * Validate if a string represents a valid road reference
 */
export function isValidRoadReference(input: string): boolean {
  return extractRoadReference(input) !== null;
}
